import moment from 'moment'
import { featureFlags } from 'config/featureFlags'

/**
 * @typedef {Object} DamageItem
 * @property {string} id
 * @property {string} value
 * @property {string} label
 * @property {string} backLabel
 * @property {string} color
 * @property {number} score
 * @property {number} timmer
 * @property {number} order
 */

/** @type {Record<string, DamageItem>} */
export const DAMAGE_LEVEL_LIST = {
  death: {
    id: 'fa57f3dc1bb2e11060d9426fe54bcb83',
    value: 'obito',
    label: 'Óbito',
    backLabel: 'Dano com óbito',
    color: '#C43B51',
    score: 0,
    timmer: 259200,
    order: 7
  },

  severe: {
    id: '5b180b9cdb76a1108394816404961964',
    value: 'grave',
    label: 'Dano Grave',
    backLabel: 'Dano grave',
    color: '#DF8D2C',
    score: 4,
    timmer: 259200,
    order: 6
  },

  moderate: {
    id: 'cbd08798db76a11083948164049619e2',
    value: 'moderado',
    label: 'Dano Moderado',
    backLabel: 'Dano moderado',
    color: '#8AA15C',
    score: 3,
    timmer: 518400,
    order: 5
  },

  mild: {
    id: '6e37039cdb76a110839481640496191f',
    value: 'leve',
    label: 'Dano Leve',
    backLabel: 'Dano leve',
    color: '#6190A4',
    score: 2,
    timmer: 604800,
    order: 4
  },

  noDamage: {
    id: '62270310dbb6a1108394816404961970',
    value: 'sem-dano',
    label: 'Sem Dano',
    backLabel: 'Sem dano',
    color: '#517BBB',
    score: 1,
    timmer: 864000,
    order: 3
  },

  nearMiss: {
    id: '0bd6cbdcdb76a110839481640496192f',
    value: 'near-miss',
    label: 'Near Miss / Quase erro',
    backLabel: 'Near miss (Quase erro)',
    color: '#B7B7B7',
    score: 1,
    timmer: 0,
    order: 2
  },

  notifiable: {
    id: '19d0c798db76a1108394816404961944',
    value: 'notifiable',
    label: 'Circunstância notificável',
    backLabel: 'Circunstância notificável',
    color: '#D4D4D4',
    score: 1,
    timmer: 0,
    order: 1
  }
} as const;


export function isSevereOrDeathDamage(damageId: string): boolean {
  return ["severe", "death"].some( (key) => DAMAGE_LEVEL_LIST[key].id === damageId);
}

export function isModerateOrSevereOrDeathDamage(DamageId): boolean {
  return isSevereOrDeathDamage(DamageId) || DamageId === DAMAGE_LEVEL_LIST.moderate.id
}

export function isNoDamageOrNearMissOrNotifiableDamage(DamageId): boolean {
  return (
    DamageId === DAMAGE_LEVEL_LIST.noDamage.id ||
    DamageId === DAMAGE_LEVEL_LIST.nearMiss.id ||
    DamageId === DAMAGE_LEVEL_LIST.notifiable.id
  )
}

export function isNotifiableDamage(damageId?: string): boolean {
  return damageId === DAMAGE_LEVEL_LIST.notifiable.id
}

export function isCircumstanceNotifiableActive(): boolean {
  return featureFlags.activeCircumstanceNotifiable
}

export function shouldShowNotifiableInDamageSelect(
  optionValue: string,
  currentDamageLevelValue?: string
): boolean {
  if (isCircumstanceNotifiableActive()) {
    return true
  }

  return (
    optionValue !== DAMAGE_LEVEL_LIST.notifiable.id ||
    optionValue === currentDamageLevelValue
  )
}

export function filterDamageLevelOptions<T extends { value: string }>(
  options: T[] | undefined,
  currentDamageLevelValue?: string
): T[] | undefined {
  if (!options) {
    return options
  }

  return options.filter(option =>
    shouldShowNotifiableInDamageSelect(option.value, currentDamageLevelValue)
  )
}

export function isNotifiableReopenBlocked(damageId?: string): boolean {
  return !isCircumstanceNotifiableActive() && isNotifiableDamage(damageId)
}

export const NOTIFIABLE_REOPEN_BLOCKED_MESSAGE =
  'Notificações classificadas como Circunstância notificável não podem ser reabertas.'

// Indica se o alerta de reabertura bloqueada deve ser exibido (status finalizada/arquivada)
export function shouldShowNotifiableReopenBlockedAlert(
  notificationStatusState: string,
  damageId?: string
): boolean {
  const isFinishedOrArchived =
    notificationStatusState === 'Finalizada' ||
    notificationStatusState === 'Arquivada'

  return isFinishedOrArchived && isNotifiableReopenBlocked(damageId)
}

// Indica se o grupo possui circunstância notificável com reabertura bloqueada pela flag
export function groupHasNotifiableReopenBlocked(
  notifications?: Array<{ damage_level?: { value?: string } }>
): boolean {
  if (isCircumstanceNotifiableActive()) {
    return false
  }

  return (
    notifications?.some(notification =>
      isNotifiableDamage(notification.damage_level?.value)
    ) ?? false
  )
}

export function isModerateOrMildDamage(DamageId): boolean {
  return (
    DamageId === DAMAGE_LEVEL_LIST.moderate.id || 
    DamageId === DAMAGE_LEVEL_LIST.mild.id
  )
}

export function requiresSubDamageLevel(damageId?: string): boolean {
  if (!damageId) {
    return false
  }

  return isModerateOrMildDamage(damageId) || isSevereOrDeathDamage(damageId)
}



export const notificatiByDamageTimmer = {
    'Sem dano': {
        maxTimeInSeconds: 864000
    },
    'Dano leve': {
        maxTimeInSeconds: 604800
    },
    'Dano moderado': {
        maxTimeInSeconds: 518400
    },
    'Dano grave': {
        maxTimeInSeconds: 259200
    },
    'Dano com ÓBITO': {
        maxTimeInSeconds: 259200
    }
}
const canHaveSimplifiedTreatmentProfiles = ['location_manager','location_qa','admin']
export const isSimplifiedTreatment = (damageValue: string,isImageRisk:boolean, userProfile: string) => {
    if (!canHaveSimplifiedTreatmentProfiles.includes(userProfile) || isImageRisk) {
        return false
    }
    const simplifiedDamages = DAMAGE_LEVELS.filter((dmg) => {
        return dmg.value === 'near-miss' || dmg.value === 'notifiable' || dmg.value === 'sem-dano'
    })
    const simplifiedElement = simplifiedDamages.find(item => item.id === damageValue)
    if (!simplifiedElement) {
        return false
    }
    return true
}

export const DAMAGE_LEVELS = Object.values(DAMAGE_LEVEL_LIST)

export const damage_level: {
    id: string
    value: string
    label: string
    color: string
    backLabel: string
    order: number
    timmer?: number
    score?:number
}[] = [
        {
            id: 'fa57f3dc1bb2e11060d9426fe54bcb83',
            value: 'obito',
            label: 'Óbito',
            backLabel: 'Dano com óbito',
            color: '#C43B51',
            score: 0,
            timmer: 259200,
            order: 7
        },
        {
            id: '5b180b9cdb76a1108394816404961964',
            value: 'grave',
            label: 'Dano Grave',
            color: '#DF8D2C',
            backLabel: 'Dano grave',
            timmer: 259200,
            score: 4,
            order: 6
        },
        {
            id: 'cbd08798db76a11083948164049619e2',
            value: 'moderado',
            label: 'Dano Moderado',
            color: '#8AA15C',
            backLabel: 'Dano moderado',
            timmer: 518400,
            score: 3,
            order: 5
        },
        {
            id: '6e37039cdb76a110839481640496191f',
            value: 'leve',
            label: 'Dano Leve',
            color: '#6190A4',
            backLabel: 'Dano leve',
            timmer: 604800,
            score: 2,
            order: 4
        },
        {
            id: '62270310dbb6a1108394816404961970',
            value: 'sem-dano',
            label: 'Sem Dano',
            color: '#517BBB',
            backLabel: 'Sem dano',
            timmer: 864000,
            score: 1,
            order: 3
        },
        {
            id: '0bd6cbdcdb76a110839481640496192f',
            value: 'near-miss',
            label: 'Near Miss / Quase erro',
            backLabel: 'Near miss (Quase erro)',
            color: '#B7B7B7',
            score: 1,
            order: 2
        },
        {
            id: '19d0c798db76a1108394816404961944',
            value: 'notifiable',
            label: 'Circunstância notificável',
            backLabel: 'Circunstância notificável',
            color: '#D4D4D4',
            score: 1,
            order: 1
        }
    ]

export const getCorIncidente = (tipo: string) => {
    return tipo?.toLowerCase().replace(' ', '-')
}

export const getDamage = (damage: string) => {
    let data = DAMAGE_LEVELS.find((d) => d.id === damage || d.label === damage)
    return data
}

export const getDamageByLabel = (damage: string) => {
    let data = DAMAGE_LEVELS.find((d) => d.label === damage)
    return data
}

export const getDamageColor = (tipo: string) => {
    return tipo?.toLowerCase().replace(' ', '-')
}

export const isNotWarningHighDamage = (damage) => {
    if (!damage) return
    if (damage === '0') return true
    let damageValue = getDamage(damage).value
    return damageValue === 'near-miss' || damageValue === 'notifiable'
}

export const getWarningCountdownColor = (deadline) => {
    let momentDeadline = moment(deadline)
    let now = moment()
    let color: string = ''
    if (momentDeadline.valueOf() >= now.valueOf()) {
        let intervalTimer = momentDeadline.diff(now)
        var duration = moment.duration(intervalTimer)

        let days = duration.days()
        if (days < 3) {
            color = DAMAGE_LEVELS[1].color
        } else if (days < 5) {
            color = DAMAGE_LEVELS[2].color
        } else if (days < 7) {
            color = DAMAGE_LEVELS[3].color
        } else if (days < 10) {
            color = DAMAGE_LEVELS[4].color
        }
    }

    return color
}

export const calculateTimeLeft = (item) => {
    // Sem data de criação, não é possível calcular prazo
    if (!item?.sys_created_on?.display_value) {
        return ''
    }
    
    let created = moment(
        item?.sys_created_on?.display_value,
        'DD/MM/YYYY HH:mm:ss'
    )
    
    // Verifica se o parsing foi correto
    if (!created.isValid()) {
        return ''
    }
    
    // Primeiro, tenta usar o tempo correto baseado no damage_level
    const damageLevel = item?.damage_level?.display_value
    let durationInSecs = null
    
    if (damageLevel && notificatiByDamageTimmer[damageLevel]) {
        durationInSecs = notificatiByDamageTimmer[damageLevel].maxTimeInSeconds
    } else if (
        item?.['damage_level.duration_secs']?.value &&
        item['damage_level.duration_secs']?.value !== ''
    ) {
        // Fallback para o campo do banco
        durationInSecs = parseInt(item['damage_level.duration_secs']?.value)
    }
    
    if (durationInSecs) {
        // Cria uma cópia para não modificar o objeto original
        let calculatedTimeLeft = created.clone().add(
            moment.duration(durationInSecs * 1000)
        )
        const result = calculatedTimeLeft.toDate() || ''
        return result
    }

    return ''
}
export const getDateTime = (date?: string): string => {
    if (!date) return ''
    const res = moment(date).format('DD/MM/YYYY - HH:mm')
    return res
}
export const getDamageLevelDuration = (item: string) => {
    if (item !== undefined && item !== '') {
        return moment(item, 'DD/MM/YYYY HH:mm')
    }
    return
}
